import {
	db,
	type Habit,
	type HabitLog,
	type MosDB,
	newId,
	type Review,
	type ReviewKind,
	stableId,
} from "./db";
import { nextUpdatedAt, save, update } from "./store";

// Habit and review writes (Dexie + outbox first). Logs and reviews have
// stable ids and are never deleted (D49): unticking sets count 0.

export type NewHabit = Pick<Habit, "name"> &
	Partial<Pick<Habit, "cadence" | "target_per_period" | "grace_days_per_week" | "is_minimum">>;

function checkHabit(h: Pick<Habit, "name" | "target_per_period" | "grace_days_per_week">) {
	const name = h.name.trim();
	if (!name || name.length > 50) throw new RangeError("Habit name must be 1-50 characters");
	if (!Number.isInteger(h.target_per_period) || h.target_per_period < 1 || h.target_per_period > 50)
		throw new RangeError("Target must be 1-50");
	if (h.grace_days_per_week !== 0 && h.grace_days_per_week !== 1)
		throw new RangeError("Grace days must be 0 or 1");
}

export async function createHabit(input: NewHabit, database: MosDB = db): Promise<Habit> {
	const now = nextUpdatedAt();
	const row: Habit = {
		id: newId(),
		cadence: "daily",
		target_per_period: 1,
		grace_days_per_week: 1,
		is_minimum: false,
		archived: false,
		...input,
		name: input.name.trim(),
		created_at: now,
		updated_at: now,
		deleted_at: null,
	};
	checkHabit(row);
	return save("habits", row, database);
}

export async function updateHabit(
	id: string,
	patch: Partial<Omit<Habit, "id" | "created_at" | "updated_at" | "deleted_at">>,
	database: MosDB = db,
): Promise<Habit> {
	const current = await database.habits.get(id);
	if (current) checkHabit({ ...current, ...patch });
	return update("habits", id, patch, database);
}

/** Sets a habit's count for a day, creating the day's log if needed. */
export async function setHabitCount(
	habitId: string,
	date: string,
	count: number,
	database: MosDB = db,
): Promise<HabitLog> {
	if (!Number.isInteger(count) || count < 0 || count > 100)
		throw new RangeError("Count must be 0-100");
	const id = await stableId(`${habitId}:${date}`);
	return database.transaction("rw", database.habit_logs, database.outbox, async () => {
		const existing = await database.habit_logs.get(id);
		if (existing) return update("habit_logs", id, { count }, database);
		const now = nextUpdatedAt();
		return save(
			"habit_logs",
			{ id, habit_id: habitId, date, count, created_at: now, updated_at: now, deleted_at: null },
			database,
		);
	});
}

/**
 * One tap (HB-1): adds a check-in until the target is met, then resets to 0,
 * so a target-1 habit toggles and a "water ×3" habit counts up.
 */
export async function tapHabit(
	habit: Habit,
	date: string,
	database: MosDB = db,
): Promise<HabitLog> {
	const id = await stableId(`${habit.id}:${date}`);
	const current = (await database.habit_logs.get(id))?.count ?? 0;
	const next = current >= habit.target_per_period ? 0 : current + 1;
	return setHabitCount(habit.id, date, next, database);
}

export type ReviewPatch = Partial<
	Pick<Review, "intention" | "energy" | "mood" | "lesson" | "score" | "minimum_day" | "summary">
>;

/** Creates or updates the review for `kind` on `date` (merged, never replaced). */
export async function saveReview(
	kind: ReviewKind,
	date: string,
	patch: ReviewPatch,
	database: MosDB = db,
): Promise<Review> {
	for (const k of ["energy", "mood"] as const) {
		const v = patch[k];
		if (v != null && (!Number.isInteger(v) || v < 1 || v > 5))
			throw new RangeError(`${k} must be 1-5`);
	}
	const id = await stableId(`${kind}:${date}`);
	return database.transaction("rw", database.reviews, database.outbox, async () => {
		const existing = await database.reviews.get(id);
		if (existing) return update("reviews", id, patch, database);
		const now = nextUpdatedAt();
		return save(
			"reviews",
			{
				id,
				kind,
				date,
				intention: "",
				energy: null,
				mood: null,
				lesson: "",
				score: null,
				minimum_day: false,
				summary: {},
				...patch,
				created_at: now,
				updated_at: now,
				deleted_at: null,
			},
			database,
		);
	});
}

/** Turns minimum-viable-day mode on or off for a day (HB-3, D50). */
export function setMinimumDay(date: string, on: boolean, database: MosDB = db): Promise<Review> {
	return saveReview("morning", date, { minimum_day: on }, database);
}
